import { ContentNugget, lottieAnimations } from '@backrs_ui/common-ui';

import { NuggetList } from '@backrs_ui/grow-ui/src/components/Nuggets/NuggetList';

import { Box, SxProps, Theme, Typography } from '@mui/material';
import Lottie from 'lottie-react';

type NuggetSectionProps = Readonly<{
  title: string;
  description?: string;
  nuggets: ContentNugget[];
  onNuggetClick: (nugget: ContentNugget) => void;
  headerButton?: React.ReactNode;
  loading?: boolean;
}>;

export const NuggetSection = ({
  title,
  description,
  nuggets,
  onNuggetClick,
  headerButton,
  loading = false,
}: NuggetSectionProps) => {
  return (
    <Box sx={containerSx}>
      <Box sx={titleContainerSx}>
        <Box sx={titleSx}>
          <Typography variant="display-sm-medium">{title}</Typography>
          {headerButton && <Box sx={largeScreenButtonSx}>{headerButton}</Box>}
        </Box>
        {description && (
          <Box sx={summaryContainerSx}>
            <Typography sx={summarySx} variant="text-sm-regular" component="div">
              {description}
            </Typography>
          </Box>
        )}
        {headerButton && <Box sx={smallScreenButtonSx}>{headerButton}</Box>}
      </Box>

      {loading && (
        <Box sx={loadingContainerSx}>
          <Lottie animationData={lottieAnimations.dotsDotting} loop />
        </Box>
      )}

      {!loading && (
        <NuggetList nuggets={nuggets} onClick={onNuggetClick} direction="scrollingRow" />
      )}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  mb: backrs.spacing['spacing-6xl'],
  borderTop: {
    xs: `1px solid ${backrs.colors['Colors/Foreground/fg-quaternary']}`,
    sm: 'none',
  },
  marginX: backrs.spacing['spacing-4xl'],
  '@media (max-height: 768px)': {
    mx: backrs.spacing['spacing-4xl'],
  },
  '@media (max-width: 414px)': {
    mx: backrs.spacing['spacing-2xl'],
  },
});

const titleContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginY: backrs.spacing['spacing-2xl'],
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  flexDirection: 'column',
});

const titleSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
};

const largeScreenButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'none', sm: 'block' },
  alignSelf: 'flex-end',
  marginBottom: backrs.spacing['spacing-xs'],
});

const smallScreenButtonSx: SxProps<Theme> = ({ backrs }) => ({
  display: { xs: 'block', sm: 'none' },
  marginTop: backrs.spacing['spacing-xl'],
});

const summarySx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  padding: 0,
  margin: 0,
});

const summaryContainerSx: SxProps<Theme> = {
  width: { xs: '100%', sm: '66%', lg: '66%' },
  padding: 0,
  margin: 0,
};

const loadingContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
  width: '100px',
  margin: '0 auto',
};
